import React, { useEffect, useState, type ReactElement } from 'react'
import { alpha, AppBar, Badge, Box, IconButton, InputAdornment, Toolbar, useMediaQuery, useTheme } from '@mui/material'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { designTokens } from '@/theme'
import { SearchIcon, ShoppingBagIcon, UserIcon } from '@/components/icons'
import { useCartItemCount } from '@/selectors/cartSelectors'
import { ThemeTextField } from '@/components/theme-text-field'
import {
    DESKTOP_TOPBAR_HEIGHT_PX,
    getMobileTopbarHeightPx,
    HEADER_MICRO_MOTION,
} from '@/layouts/header-metrics'
import { storefrontJoin, stripStorefrontPrefix, useStorefrontBasePath } from '@/utils/storefront-paths'
import { useRestaurant } from '@/hooks/use-restaurant'

function clamp01(v: number): number {
    return Math.min(1, Math.max(0, v))
}

const BRAND_WIDTH_PX = 200
const BRAND_HEIGHT_PX = 46

type BrandMarkProps = {
    showLogo: boolean
    logoUrl: string | null
    restaurantLabel: string
    onLogoError: () => void
    onNavigateHome: () => void
    interactive: boolean
}

function BrandMark({
    showLogo,
    logoUrl,
    restaurantLabel,
    onLogoError,
    onNavigateHome,
    interactive,
}: BrandMarkProps): ReactElement {
    const theme = useTheme()

    if (showLogo && logoUrl) {
                return (
            <img
                src={logoUrl}
                alt={restaurantLabel}
                loading="eager"
                decoding="async"
                referrerPolicy="no-referrer"
                width={BRAND_WIDTH_PX}
                height={BRAND_HEIGHT_PX}
                onError={onLogoError}
                onClick={interactive ? onNavigateHome : undefined}
                style={{
                    objectFit: "contain",
                    display: "block",
                    width: BRAND_WIDTH_PX,
                    height: BRAND_HEIGHT_PX,
                    pointerEvents: interactive ? "auto" : "none",
                    cursor: interactive ? "pointer" : undefined,
                }}
            />
        )
    }

    return (
        <Box
            component={interactive ? 'button' : 'span'}
            type={interactive ? 'button' : undefined}
            onClick={interactive ? onNavigateHome : undefined}
            sx={{
                m: 0,
                p: 0,
                border: 'none',
                background: 'none',
                display: 'block',
                maxWidth: BRAND_WIDTH_PX,
                width: 'max-content',
                height: BRAND_HEIGHT_PX,
                lineHeight: `${BRAND_HEIGHT_PX}px`,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: theme.typography.fontFamily,
                fontWeight: 700,
                fontSize: { xs: '1.125rem', md: '1.25rem' },
                color: theme.palette.text.primary,
                cursor: interactive ? 'pointer' : 'inherit',
                textAlign: interactive ? 'left' : 'center',
            }}
        >
            {restaurantLabel}
        </Box>
    )
}

type TopbarProps = {
    /** 0 = full mobile header, 1 = collapsed (scroll progress). Ignored on desktop. */
    mobileCollapseProgress?: number
}

function Topbar({ mobileCollapseProgress = 0 }: TopbarProps) {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
    const p = isMobile ? clamp01(mobileCollapseProgress) : 0
    const navigate = useNavigate()
    const basePath = useStorefrontBasePath()
    const [searchParams, setSearchParams] = useSearchParams()
    const location = useLocation()
    const onHomepage = stripStorefrontPrefix(location.pathname) === '/'
    const cartCount = useCartItemCount()
    const searchKeyword = searchParams.get('q') ?? ''
    const { logo_url, brand_name, name } = useRestaurant()
    const restaurantLabel = (brand_name || name || 'Restaurant').trim()
    const [logoFailed, setLogoFailed] = useState(false)

    useEffect(() => {
        setLogoFailed(false)
    }, [logo_url])

    const showLogo = Boolean(logo_url && !logoFailed)
    const goHome = () => navigate(storefrontJoin(basePath, '/'))

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value
        if (v && onHomepage) {
            // The homepage embeds the dishes feed below the hero and CMS sections, so a
            // keyword typed here filters results the customer cannot see. Take the same
            // route the hero's "Browse Menu" uses: the feed is the top of /dishes and the
            // router's pathname-change scroll lands the viewport on it. Later keystrokes
            // are already on /dishes and keep the existing setSearchParams behaviour.
            const next = new URLSearchParams(searchParams)
            next.set('q', v)
            navigate({ pathname: storefrontJoin(basePath, '/dishes'), search: `?${next.toString()}` })
            return
        }
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            if (v) next.set('q', v)
            else next.delete('q')
            return next
        })
    }

    const mobileBarHeight = getMobileTopbarHeightPx(p)
    const logoOpacity = prefersReducedMotion ? 1 : 1 - p
    const logoScale = prefersReducedMotion ? 1 : 1 - 0.2 * p
    const logoTranslateY = prefersReducedMotion ? 0 : -12 * p

    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{
                height: { xs: mobileBarHeight, md: DESKTOP_TOPBAR_HEIGHT_PX },
                minHeight: { xs: mobileBarHeight, md: DESKTOP_TOPBAR_HEIGHT_PX },
                padding: 0,
                overflow: 'hidden',
                border: 'none',
                background: alpha(theme.palette.background.default, 0.85),
                backdropFilter: 'blur(14px)',
                WebkitBackdropFilter: 'blur(14px)',
                boxShadow: designTokens.shadows.soft,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                transition: {
                    xs: `box-shadow ${HEADER_MICRO_MOTION}`,
                    md: `all ${designTokens.motion.slow}`,
                },
            }}
        >
            <Toolbar
                sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', md: 'space-between' },
                    alignItems: 'center',
                    height: '100%',
                    minHeight: { xs: 0, md: undefined },
                    px: 2,
                    maxWidth: { xs: '100%', md: 1200 },
                    mx: { md: 'auto' },
                    width: '100%',
                }}
            >
                {isMobile ? (
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Box
                            component="button"
                            type="button"
                            onClick={goHome}
                            aria-label="Home"
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                zIndex: 1,
                                border: 0,
                                padding: 0,
                                margin: 0,
                                background: 'transparent',
                                cursor: 'pointer',
                                WebkitTapHighlightColor: 'transparent',
                            }}
                        />
                        <Box
                            aria-hidden
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                zIndex: 2,
                                transform: `translate3d(-50%, -50%, 0) scale(${logoScale}) translate3d(0, ${logoTranslateY}px, 0)`,
                                opacity: logoOpacity,
                                pointerEvents: 'none',
                                willChange: prefersReducedMotion ? undefined : 'transform, opacity',
                            }}
                        >
                            <BrandMark
                                showLogo={showLogo}
                                logoUrl={logo_url}
                                restaurantLabel={restaurantLabel}
                                onLogoError={() => setLogoFailed(true)}
                                onNavigateHome={goHome}
                                interactive={false}
                            />
                        </Box>
                    </Box>
                ) : (
                    <Box component="span" sx={{ flexShrink: 0 }}>
                        <BrandMark
                            showLogo={showLogo}
                            logoUrl={logo_url}
                            restaurantLabel={restaurantLabel}
                            onLogoError={() => setLogoFailed(true)}
                            onNavigateHome={goHome}
                            interactive
                        />
                    </Box>
                )}
                <Box
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        flex: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                        px: 2,
                        maxWidth: 600,
                        mx: 'auto',
                    }}
                >
                    <ThemeTextField
                        placeholder="Search..."
                        size="small"
                        value={searchKeyword}
                        onChange={handleSearchChange}
                        sx={{ minHeight: 'auto !important', width: '100%' }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ width: 20, height: 20 }} />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <IconButton
                        onClick={() => navigate(storefrontJoin(basePath, '/checkout'))}
                        aria-label={`Cart with ${cartCount} items`}
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.text.primary,
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                            },
                            '& svg': {
                                width: 26,
                                height: 26,
                            },
                        }}
                    >
                        <Badge
                            badgeContent={cartCount}
                            color="secondary"
                            showZero={false}
                            slotProps={{
                                badge: {
                                    sx: (t) => ({
                                        minWidth: 20,
                                        height: 20,
                                        fontSize: t.typography.caption.fontSize,
                                    }),
                                },
                            }}
                        >
                            <ShoppingBagIcon />
                        </Badge>
                    </IconButton>
                    <IconButton
                        onClick={() => navigate(storefrontJoin(basePath, '/my-account'))}
                        aria-label="Profile"
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.text.primary,
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                            },
                            '& svg': {
                                width: 26,
                                height: 26,
                            },
                        }}
                    >
                        <UserIcon />
                    </IconButton>
                </Box>
            </Toolbar>
        </AppBar>
    )
}

export default Topbar
